'use client'
import { cn } from '@/lib/utils'
import { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts'

interface ReadMoreTextProps {
  content: ReactNode
  contentElement?: 'div' | 'p'
  lines?: number
  defaultLineHeight?: number
  readMoreLabel?: string
  readLessLabel?: string
  /** When true, the toggle can collapse the text back after expanding. */
  collapsible?: boolean
  onCollapse?: () => void
  readMoreButtonClassName?: string
  className?: string
}

export default function ReadMoreText({
  content,
  contentElement: ContentElement = 'p',
  lines = 3,
  readMoreLabel = 'Read more',
  readLessLabel = 'Read less',
  collapsible = false,
  onCollapse,
  readMoreButtonClassName = '',
  className = '',
}: ReadMoreTextProps) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const fullMeasureRef = useRef<HTMLParagraphElement>(null)
  const clampMeasureRef = useRef<HTMLParagraphElement>(null)

  const checkOverflow = useCallback(() => {
    const fullMeasureEl = fullMeasureRef.current
    const clampMeasureEl = clampMeasureRef.current
    const container = containerRef.current
    if (!fullMeasureEl || !clampMeasureEl || !container) return

    const containerWidth = container.getBoundingClientRect().width
    if (containerWidth <= 0) return

    fullMeasureEl.style.width = `${containerWidth}px`
    clampMeasureEl.style.width = `${containerWidth}px`

    const fullHeight = fullMeasureEl.getBoundingClientRect().height
    const clampHeight = clampMeasureEl.getBoundingClientRect().height

    setIsOverflow(fullHeight > clampHeight + 0.5)
  }, [])

  const handleToggle = () => {
    if (expanded && collapsible) onCollapse?.()

    setExpanded((prev) => (collapsible ? !prev : true))
  }

  const debouncedCheckOverflow = useDebounceCallback(checkOverflow, 150, {
    leading: false,
    trailing: true,
  })

  useResizeObserver({
    ref: containerRef as RefObject<HTMLElement>,
    onResize: () => debouncedCheckOverflow(),
  })

  useEffect(() => {
    checkOverflow()
  }, [content, lines, checkOverflow])

  return (
    <div ref={containerRef} className="relative">
      <ContentElement
        className={cn(
          'wrap-break-word whitespace-pre-wrap',
          !expanded && `line-clamp-${lines}`,
          className,
        )}
      >
        {content}
      </ContentElement>

      <ContentElement
        ref={fullMeasureRef}
        aria-hidden="true"
        className={cn(
          'pointer-events-none invisible absolute top-0 left-0 wrap-break-word whitespace-pre-wrap',
          className,
        )}
      >
        {content}
      </ContentElement>

      <ContentElement
        ref={clampMeasureRef}
        aria-hidden="true"
        className={cn(
          'pointer-events-none invisible absolute top-0 left-0 wrap-break-word whitespace-pre-wrap',
          `line-clamp-${lines}`,
          className,
        )}
      >
        {content}
      </ContentElement>

      {isOverflow && (!expanded || collapsible) && (
        <button
          type="button"
          onClick={handleToggle}
          className={cn(
            'text-vive-primary-40 body1-omni-styles mt-2 cursor-pointer hover:underline',
            readMoreButtonClassName,
          )}
        >
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      )}
    </div>
  )
}
